import { CSSProperties, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cleanTextForSearch } from '../biz/markdownLite';
import backend from '../backend';
import { SquiggleItem, SquiggleTypes } from '../types/types';
import './LinkHoverPreview.css';

interface PreviewData {
  name: string;
  text: string;
  imageUrl?: string;
  x: number;
  y: number;
  type: SquiggleTypes;
}

const PADDING = 20;
const CURSOR_OFFSET_X = 14;
const CURSOR_OFFSET_Y = 18;
const PREFERRED_WIDTH = 320;
const TEXT_HEIGHT = 96;
const IMAGE_HEIGHT = 156;
const ESTIMATED_HEIGHT_WITH_IMAGE = Math.max(TEXT_HEIGHT, IMAGE_HEIGHT);
const PREVIEW_DELAY_MS = 250;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function getAnchor(e: MouseEvent<HTMLElement>) {
  const target = e.target;
  if (!(target instanceof HTMLElement)) {
    return null;
  }

  const anchor = target.closest('a[data-id]');
  return anchor instanceof HTMLAnchorElement && anchor.dataset.id ? anchor : null;
}

export function useLinkHoverPreview(itemById: Map<string, SquiggleItem>, imageSeed: number) {
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const timerRef = useRef<number | undefined>(undefined);
  const hoveredIdRef = useRef<string | null>(null);
  const positionRef = useRef<{ x: number; y: number } | null>(null);
  const platformHasHover = useMemo(() => window.matchMedia('(hover: hover)').matches, []);

  const hide = useCallback(() => {
    hoveredIdRef.current = null;
    positionRef.current = null;
    if (timerRef.current !== undefined) {
      window.clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
    setPreview(null);
  }, []);

  const show = useCallback((id: string, x: number, y: number) => {
    const item = itemById.get(id);
    if (!item) {
      hide();
      return;
    }

    const excerpt = cleanTextForSearch(item.text).slice(0, 250);
    const text = item.text.length > 250 ? `${excerpt}…` : excerpt;
    const imageUrl = item.images.length > 0 ? backend.getImageUrl(item.images[0].imageFileName, imageSeed) : undefined;

    setPreview({ name: item.name, text, imageUrl, x, y, type: item.type });
  }, [hide, imageSeed, itemById]);

  const schedule = useCallback((id: string, x: number, y: number) => {
    hoveredIdRef.current = id;
    positionRef.current = { x, y };
    if (timerRef.current !== undefined) {
      window.clearTimeout(timerRef.current);
    }
    timerRef.current = window.setTimeout(() => {
      if (hoveredIdRef.current === id && positionRef.current) {
        show(id, positionRef.current.x, positionRef.current.y);
      }
    }, PREVIEW_DELAY_MS);
  }, [show]);

  const onMouseEnter = useCallback((e: MouseEvent<HTMLElement>) => {
    if (!platformHasHover) {
      return;
    }
    const id = getAnchor(e)?.dataset.id;
    if (!id) {
      return;
    }

    if (hoveredIdRef.current !== id) {
      hide();
    }
    schedule(id, e.clientX, e.clientY);
  }, [hide, schedule, platformHasHover]);

  const onMouseMove = useCallback((e: MouseEvent<HTMLElement>) => {
    if (!platformHasHover) {
      return;
    }

    const id = getAnchor(e)?.dataset.id;
    if (!id || hoveredIdRef.current !== id) {
      return;
    }

    positionRef.current = { x: e.clientX, y: e.clientY };
    setPreview(current => current ? { ...current, x: e.clientX, y: e.clientY } : current);
  }, [platformHasHover]);

  const onMouseLeave = useCallback((e: MouseEvent<HTMLElement>) => {
    if (!platformHasHover) {
      return;
    }

    const anchor = getAnchor(e);
    if (!anchor) {
      return;
    }

    const relatedTarget = e.relatedTarget;
    if (relatedTarget instanceof HTMLElement && anchor.contains(relatedTarget)) {
      return;
    }

    hide();
  }, [hide, platformHasHover]);

  useEffect(() => () => {
    if (timerRef.current !== undefined) {
      window.clearTimeout(timerRef.current);
    }
  }, []);

  const style = useMemo<CSSProperties | undefined>(() => {
    if (!preview) {
      return undefined;
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const rightSpace = viewportWidth - (preview.x + CURSOR_OFFSET_X) - PADDING;
    const leftSpace = preview.x - CURSOR_OFFSET_X - PADDING;
    const placeRight = rightSpace >= leftSpace;
    const availableWidth = placeRight ? rightSpace : leftSpace;
    const width = Math.min(availableWidth, PREFERRED_WIDTH);

    const left = placeRight
      ? preview.x + CURSOR_OFFSET_X
      : preview.x - CURSOR_OFFSET_X - width;
    const clampedLeft = clamp(left, PADDING, viewportWidth - PADDING - width);

    const estimatedHeight = preview.imageUrl ? ESTIMATED_HEIGHT_WITH_IMAGE : TEXT_HEIGHT;
    const belowSpace = viewportHeight - (preview.y + CURSOR_OFFSET_Y) - PADDING;
    const aboveSpace = preview.y - CURSOR_OFFSET_Y - PADDING;
    const placeBelow = belowSpace >= estimatedHeight || belowSpace >= aboveSpace;
    const top = placeBelow
      ? preview.y + CURSOR_OFFSET_Y
      : preview.y - CURSOR_OFFSET_Y - estimatedHeight;
    const clampedTop = clamp(top, PADDING, viewportHeight - PADDING - estimatedHeight);

    return {
      left: `${clampedLeft}px`,
      top: `${clampedTop}px`,
      width: `${width}px`,
      maxWidth: `${width}px`,
    };
  }, [preview]);

  return {
    preview,
    style,
    onMouseEnter,
    onMouseMove,
    onMouseLeave,
  };
}

export function Preview({
  preview,
  style,
}: {
  preview: PreviewData | null;
  style?: CSSProperties;
}) {
  if (!preview) {
    return null;
  }

  return (
    <div className={"linkhoverpreview " + preview.type} style={style}>
      <div className="linkhoverpreviewcontent">
        <div className="linkhoverpreviewname">{preview.name}</div>
        {preview.imageUrl && <img className="linkhoverpreviewimage" src={preview.imageUrl} alt={preview.name} />}
        <div className={"linkhoverpreviewtext " + (preview.text ? "" : "empty")}>{preview.text || "No description available"}</div>
      </div>
    </div>
  );
}
